/**
 * CommunityNotFound — DEV-947
 *
 * Rendered by CommunitySlugRoute when a slug URL fails to resolve. Names
 * the path the user attempted, explains the likely causes, and offers
 * escape routes.
 *
 * Replaces the prior silent navigate('/communities') redirect — which
 * (a) was called during render, a React anti-pattern, and (b) gave the
 * user no signal about what went wrong.
 *
 * All strings via useAppTranslation('pages'); falls back to inline English
 * defaults so the page still reads sensibly during the brief window
 * between code ship and translator pass on the new keys.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';

export interface CommunityNotFoundProps {
  /** The path segments the user attempted, e.g. "usa/foo" */
  attemptedPath: string;
}

const CommunityNotFound: React.FC<CommunityNotFoundProps> = ({ attemptedPath }) => {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();

  const displayPath = `/${attemptedPath}`;

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        color: 'var(--text-primary)',
      }}
    >
      <div
        style={{
          maxInlineSize: 480,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 48,
            lineHeight: 1,
            color: 'var(--text-tertiary)',
            marginBlockEnd: 16,
            fontVariantNumeric: 'tabular-nums',
          }}
          aria-hidden="true"
        >
          404
        </div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 500,
            margin: '0 0 12px',
            color: 'var(--text-primary)',
          }}
        >
          {t('communityNotFound.title', { defaultValue: 'Community not found' })}
        </h1>
        <p
          style={{
            margin: '0 0 8px',
            fontSize: 14,
            lineHeight: 1.55,
            color: 'var(--text-secondary)',
          }}
        >
          {t('communityNotFound.body', {
            defaultValue:
              'No community at {{path}}. The community may have been renamed, or the URL may be a typo.',
            path: displayPath,
          })}
        </p>
        <p
          style={{
            margin: '20px 0 8px',
            fontSize: 12,
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          }}
        >
          {displayPath}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            marginBlockStart: 24,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/communities')}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--info-text)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {t('communityNotFound.browseLink', { defaultValue: 'Browse all communities' })}
          </button>
          {/* DEV-1452: the "Go back" (navigate(-1)) escape hatch is removed —
              it was dead on a direct/external arrival (empty history) anyway.
              The named destination above is the reliable path out. */}
        </div>
      </div>
    </div>
  );
};

export default CommunityNotFound;
